import i18n from 'i18n'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { giftCardsAPI } from 'api/giftCards'
import { branchChanged } from './branchSlice'

export interface GiftCard {
 id: number
 code: string
 initial_balance: number
 current_balance: number
 branch_id: number | null
 purchased_by_customer_id: number | null
 redeemed_by_customer_id: number | null
 sale_id: number | null
 expires_at: string | null
 status: 'active' | 'partially_used' | 'used' | 'expired' | 'cancelled'
 notes: string | null
 created_at: string
 branch?: { id: number; name: string }
 purchased_by?: { id: number; name: string }
 redeemed_by?: { id: number; name: string }
}

interface GiftCardsState {
 data: GiftCard[]
 statistics: {
 total_issued: number
 total_active: number
 total_used: number
 total_cancelled: number
 total_outstanding_balance: number
 total_redeemed_amount: number
 } | null
 loading: boolean
 error: string | null
}

const initialState: GiftCardsState = {
 data: [],
 statistics: null,
 loading: false,
 error: null,
}

export const fetchGiftCards = createAsyncThunk(
 'giftCards/fetchAll',
 async (params: Record<string, any> | undefined, { rejectWithValue }) => {
 try {
 const res = await giftCardsAPI.list(params)
 return res.data
 } catch (e: any) {
 return rejectWithValue(e.response?.data?.message || i18n.t('errors.giftCards.fetchFailed'))
 }
 }
)

export const fetchGiftCardStatistics = createAsyncThunk(
 'giftCards/fetchStatistics',
 async (_, { rejectWithValue }) => {
 try {
 const res = await giftCardsAPI.statistics()
 return res.data.data
 } catch (e: any) {
 return rejectWithValue(e.response?.data?.message || i18n.t('errors.giftCards.statsFailed'))
 }
 }
)

export const createGiftCard = createAsyncThunk(
 'giftCards/create',
 async (data: Record<string, any>, { rejectWithValue }) => {
 try {
 const res = await giftCardsAPI.create(data)
 return res.data
 } catch (e: any) {
 return rejectWithValue(e.response?.data?.message || i18n.t('errors.giftCards.createFailed'))
 }
 }
)

export const cancelGiftCard = createAsyncThunk(
 'giftCards/cancel',
 async (id: number, { rejectWithValue }) => {
 try {
 const res = await giftCardsAPI.cancel(id)
 return res.data
 } catch (e: any) {
 return rejectWithValue(e.response?.data?.message || i18n.t('errors.giftCards.cancelFailed'))
 }
 }
)

const giftCardsSlice = createSlice({
 name: 'giftCards',
 initialState,
 reducers: {},
 extraReducers: (builder) => {
 builder
 .addCase(fetchGiftCards.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(fetchGiftCards.fulfilled, (state, action) => {
 state.loading = false
 state.data = action.payload.data?.data ?? action.payload.data ?? []
 })
 .addCase(fetchGiftCards.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 .addCase(fetchGiftCardStatistics.fulfilled, (state, action) => {
 state.statistics = action.payload
 })
 .addCase(createGiftCard.fulfilled, (state, action) => {
 const card = action.payload.data
 if (card) state.data.unshift(card)
 })
 .addCase(cancelGiftCard.fulfilled, (state, action) => {
 const card = action.payload.data
 if (card) {
 const idx = state.data.findIndex((c) => c.id === card.id)
 if (idx >= 0) state.data[idx] = card
 }
 })
 .addCase(branchChanged, (state) => {
 Object.assign(state, initialState)
 })
 },
})

export default giftCardsSlice.reducer
